<template>
  <div class="sign-in">
    <SignBanner result="auth" />
    <ModalComponentBody>
      <SignTitle>
        {{ messages?.entry }}
      </SignTitle>
      <SignText>
        {{ messages?.authorizationText }}
      </SignText>
      <form class="sign-in-form" @submit.prevent="handlerSubmit">
        <DefPhone
          :initial-country="initialCountry"
          :placeholder="messages?.phoneNumber"
          name="phone"
          required
          :is-show-error="Boolean(errors?.signInByPhone)"
          :error-text="errors?.signInByPhone"
          @countrychange="countrychange"
        />
        <DefInputPassword
          v-if="!isUkrainePhone"
          :placeholder="messages?.password"
          name="password"
          :is-show-error="Boolean(errors && errors.signInByPhoneAndPassword)"
          required
        >
          {{ errors?.signInByPhoneAndPassword }}
        </DefInputPassword>

        <ForgetPasswordBtn v-if="!isUkrainePhone" />

        <DefButtonPrimary type="submit"> {{ messages?.enter }} </DefButtonPrimary>
        <GoSignUp />
      </form>
    </ModalComponentBody>
  </div>
</template>

<script setup lang="ts">
import SignBanner from '@components/Cabinet/Entry/SignBanner.vue';
import ModalComponentBody from '@use/useModal/ModalComponentBody.vue';
import SignTitle from '@components/Cabinet/Entry/SignTitle.vue';
import SignText from '@components/Cabinet/Entry/SignText.vue';
import { computed, onBeforeMount, reactive } from 'vue';
import DefPhone from '@components/Def/DefPhone/DefPhone.vue';
import DefInputPassword from '@components/Def/DefInput/DefInputPassword.vue';
import DefButtonPrimary from '@components/Def/DefButtonPrimary/DefButtonPrimary.vue';
import GoSignUp from '@components/Cabinet/Entry/SignIn/GoSignUp.vue';
import Recaptcha3 from '@/utils/Recaptcha3';
import ForgetPasswordBtn from '@components/Cabinet/Entry/ForgotPassword/ForgotPasswordBtn.vue';
import SignInConfirmPhone from '@components/Cabinet/Entry/SignIn/SignInConfirmPhone.vue';
import useModal from '@use/useModal/useModal';
import { useResultsStore } from '@/storePinia/stores/results';
import { usePersonalStore } from '@/storePinia/stores/personal';
import useBlackList from '@use/useBlackList';

const modal = useModal();
const { ensureNotBlocked } = useBlackList();
const resultsStore = useResultsStore();
const personalStore = usePersonalStore();

const auth = computed(() => personalStore.routes.auth?.data);
const messages = computed(() => resultsStore.messages?.entry);
const errors = computed(() => personalStore.errors);
const signInBanners = computed(() => resultsStore.signInBanners);

const getData = async () => {
  if (!auth.value) return;

  const { action, api } = auth.value;

  await personalStore.getData({
    action,
    api,
  });
};

const getBanner = () => {
  signInBanners.value?.find((el) => {
    if (el.signInTypeKeys.find((key) => key === 'auth')) {
      return true;
    }

    return false;
  });
};

const countryISOCode = computed(() => resultsStore.getSelectedCountry?.UF_COUNTRY_ISO_CODE);
const initialCountry = computed(() => {
  if (countryISOCode.value) {
    return countryISOCode.value;
  }
  return undefined;
});

const componentState = reactive({
  selectedCountryCode: '',
});

const isUkrainePhone = computed(() => {
  if (componentState.selectedCountryCode === '' && initialCountry.value?.toLowerCase() === 'ua') {
    return true;
  }
  return componentState.selectedCountryCode === 'ua';
});

const countrychange = (countryCode: string) => {
  if (countryCode) {
    componentState.selectedCountryCode = countryCode.toLowerCase();
  }
};

const handlerSubmit = async (e: SubmitEvent): void => {
  const formData: FormData = new FormData(e.target as HTMLFormElement);

  if (!(await ensureNotBlocked(formData.get('phone') as string))) return;

  const token = await Recaptcha3.getSubmitToken();
  formData.append('g-recaptcha-response', token);

  if (isUkrainePhone.value) {
    // console.log('UKRAINE');
    const response = await personalStore.signInByPhone(formData);

    if (response) {
      modal.show(
        SignInConfirmPhone,
        { result: { formData } },
        {
          width: '900px',
          backgroundClose: false,
        }
      );
    }
  } else {
    const response = await personalStore.signInByPhonePassword(formData);

    if (response?.status === true) {
      window.location.reload();
    }
  }
};

onBeforeMount(async () => {
  await getData();
  getBanner();
});
</script>

<style lang="scss" scoped>
.sign-in {
  @include point-md {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  :deep(.def-button-primary) {
    margin-top: 4px;
  }
}
.sign-in-form {
  display: grid;
  grid-row-gap: 12px;
}
</style>
